import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import imageCompression from 'browser-image-compression';

const CONSULT_SESSION_KEY = 'consult_client_session_id';
const PENDING_PRODUCT_URL_KEY = 'consult_pending_product_url';

const patternPlaceholderSvg = `
<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">
  <rect fill="#1a1a1a" width="400" height="400"/>
  <rect fill="#2c2b2b" x="40" y="40" width="120" height="120" rx="8"/>
  <rect fill="#333" x="200" y="80" width="160" height="100" rx="8"/>
  <rect fill="#252525" x="80" y="220" width="240" height="140" rx="8"/>
  <text x="200" y="385" fill="#787878" font-family="sans-serif" font-size="13" text-anchor="middle">Pattern image coming soon</text>
</svg>`;

export const PATTERN_PLACEHOLDER = `data:image/svg+xml,${encodeURIComponent(patternPlaceholderSvg)}`;

const PATTERN_IMAGE_EXTENSIONS = ['jpg', 'webp', 'png', 'jpeg'];

function getPatternImageExtension(url) {
  const match = url.match(/\.(jpe?g|png|webp)(?:[?#].*)?$/i);
  return match ? match[1].toLowerCase() : null;
}

function stripPatternImageExtension(url) {
  return url.replace(/\.(jpe?g|png|webp)(?:[?#].*)?$/i, '');
}

export function handlePatternImageError(event) {
  const img = event.target;
  const catalogSrc = img.dataset.catalogSrc || img.getAttribute('src') || '';

  if (!img.dataset.catalogSrc) {
    img.dataset.catalogSrc = catalogSrc;
  }

  const tried = img.dataset.triedExts ? img.dataset.triedExts.split(',') : [];
  const failedExt = getPatternImageExtension(img.src);
  if (failedExt && !tried.includes(failedExt)) {
    tried.push(failedExt);
  }

  const base = stripPatternImageExtension(catalogSrc);
  const nextExt = PATTERN_IMAGE_EXTENSIONS.find((ext) => !tried.includes(ext));

  if (!base || !nextExt) {
    img.onerror = null;
    img.src = PATTERN_PLACEHOLDER;
    return;
  }

  tried.push(nextExt);
  img.dataset.triedExts = tried.join(',');
  img.src = `${base}.${nextExt}`;
}

export const CONSULT_FUNCTION_URL =
  process.env.REACT_APP_CONSULT_CAMO_URL ||
  'https://consultcamo-yepdp67tpq-uc.a.run.app';

export function getClientSessionId() {
  let sessionId = localStorage.getItem(CONSULT_SESSION_KEY);
  if (!sessionId) {
    sessionId = `consult_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(CONSULT_SESSION_KEY, sessionId);
  }
  return sessionId;
}

export function formatPriceTier(tier) {
  if (tier === 1) return '$';
  if (tier === 2) return '$$';
  if (tier === 3) return '$$$';
  return '';
}

export async function uploadConsultPhoto(file, userId) {
  const compressionOptions = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true
  };
  const compressed = await imageCompression(file, compressionOptions);
  const timestamp = Date.now();
  const photoRef = ref(storage, `consult_photos/${userId}/${timestamp}.jpg`);
  await uploadBytes(photoRef, compressed);
  return getDownloadURL(photoRef);
}

export async function fetchConsultRecommendations({
  state,
  county,
  photoUrl,
  userId,
  idToken
}) {
  const headers = { 'Content-Type': 'application/json' };
  if (idToken) {
    headers.Authorization = `Bearer ${idToken}`;
  }

  const response = await fetch(CONSULT_FUNCTION_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      state,
      county: county || null,
      photoUrl: photoUrl || null,
      userId: userId || null,
      clientSessionId: getClientSessionId()
    })
  });

  const data = await response.json();
  if (!response.ok) {
    const message = [data.error, data.details].filter(Boolean).join(': ');
    throw new Error(message || 'Failed to get recommendations');
  }
  return data;
}

export function storePendingProductUrl(url) {
  sessionStorage.setItem(PENDING_PRODUCT_URL_KEY, url);
}

export function consumePendingProductUrl() {
  const url = sessionStorage.getItem(PENDING_PRODUCT_URL_KEY);
  if (url) {
    sessionStorage.removeItem(PENDING_PRODUCT_URL_KEY);
  }
  return url;
}

export function openProductUrl(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}
